import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Database, 
  Mail, 
  Search, 
  Zap, 
  BarChart, 
  Calendar, 
  Clock, 
  CheckCircle, 
  X, 
  Users, 
  TrendingUp, 
  Building,
  Lightbulb,
  MessageSquare,
  Linkedin,
  Target,
  Award
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function BeyondDataConsulting() {
  const [showCalculator, setShowCalculator] = useState(false);
  const [showTestimonial, setShowTestimonial] = useState(false);
  
  // Updated pricing structure
  const setupFee = 2300;
  const monthlyLeadsHandled = 800;
  const monthlySavings = 15000; // Average monthly value from leads
  const annualSavings = monthlySavings * 12;
  const roi = Math.round(((annualSavings * 0.05) - setupFee) / setupFee * 100); // Using minimum 5% commission
  const paybackPeriod = Math.ceil(setupFee / (monthlySavings * 0.05)); // days to recoup setup fee
  
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Database className="w-16 h-16 text-golden animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Beyond Data <span className="text-golden">Consulting</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Automated Lead Generation System
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              A comprehensive lead generation solution designed specifically for Beyond Data Consulting to connect with high-quality prospects at scale, driving business growth and consulting success.
            </p>
          </div>
        </motion.div>

        {/* Introduction Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">The Automated Lead Generation System</h2>
          <p className="text-gray-300 mb-6">
            Our system leverages cutting-edge automation to deliver massive personalized outreach campaigns, sending up to <span className="text-golden font-semibold">2,000 highly targeted emails daily</span> to companies that need your data consulting services.
          </p>
          <p className="text-gray-300 mb-6">
            What makes this approach different is the level of personalization. We don't just send emails to random contacts—we specifically reach out to companies that are actively seeking data consulting services, ensuring our outreach is relevant and timely.
          </p>
          <p className="text-gray-300">
            The system works 24/7, continuously generating leads while you focus on your core consulting activities. It's like having an entire business development team working around the clock, but at a fraction of the cost.
          </p>
        </MouseFollowEffect>

        {/* System Components */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">System Components</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Linkedin className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">LinkedIn Sales Navigator</h3>
                  <p className="text-gray-300">
                    We use LinkedIn Sales Navigator to identify and target the perfect prospects for Beyond Data Consulting. This powerful tool allows us to filter by industry, company size, job title, and more.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Zap className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Make.com</h3>
                  <p className="text-gray-300">
                    Make.com serves as the automation backbone of our system, connecting all components and orchestrating the entire workflow. It handles everything from data processing to campaign management.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Mail className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Instantly.ai</h3>
                  <p className="text-gray-300">
                    Instantly.ai handles the email delivery with industry-leading deliverability rates. It manages email warming, sending schedules, and follow-ups automatically.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Search className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Any Mail Finder</h3>
                  <p className="text-gray-300">
                    This specialized tool helps us discover verified email addresses for your target prospects with exceptional accuracy. It ensures we're reaching the right people.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* System Structure */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">System Structure</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="mb-6">
              <img 
                src="/System 3/Screenshot 2025-02-27 at 16.49.22.png" 
                alt="Lead Generation System Structure" 
                className="w-full rounded-xl"
              />
            </div>
            <h3 className="text-xl font-bold mb-4 text-golden">How It All Works Together</h3>
            <ol className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">1.</span>
                <span><strong>Prospect Identification:</strong> LinkedIn Sales Navigator identifies ideal prospects based on your target customer profile.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">2.</span>
                <span><strong>Data Enrichment:</strong> Any Mail Finder discovers and verifies email addresses for these prospects.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">3.</span>
                <span><strong>Automation Orchestration:</strong> Make.com connects all tools and manages the workflow between platforms.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">4.</span>
                <span><strong>Personalized Outreach:</strong> Instantly.ai delivers highly personalized emails with automatic follow-ups.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">5.</span>
                <span><strong>Response Management:</strong> The system tracks responses and alerts your team when prospects engage.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">6.</span>
                <span><strong>Continuous Optimization:</strong> We analyze results and refine the approach for ever-improving performance.</span>
              </li>
            </ol>
          </div>
        </div>

        {/* Campaign Metrics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Proven Results</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 text-golden">Campaign Metrics</h3>
              <div className="mb-6">
                <img 
                  src="/System 3/Metrics.png" 
                  alt="Campaign Metrics" 
                  className="w-full rounded-xl"
                />
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Open Rate:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">78%</span>
                    <span className="text-green-500 ml-2 text-sm">(+46% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Reply Rate:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">15%</span>
                    <span className="text-green-500 ml-2 text-sm">(+10% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Meeting Conversion:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">4.2%</span>
                    <span className="text-green-500 ml-2 text-sm">(+2.7% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Cost Per Meeting:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">$38</span>
                    <span className="text-green-500 ml-2 text-sm">(vs. $250+ industry avg.)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 text-golden">What This Means for Beyond Data Consulting</h3>
              <div className="space-y-4 text-gray-300">
                <p>
                  Based on these metrics and your current market position, you could expect:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>40-60 qualified meetings per month</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>4-10 new clients per month</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Estimated ROI of 250-350% within 90 days</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Significant brand awareness boost in your target market</span>
                  </li>
                </ul>
                <p className="mt-4">
                  These results would transform your growth trajectory, creating a predictable and scalable lead generation engine.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Your <span className="text-golden">Investment</span></h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Complete lead generation system setup</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Custom targeting for your ideal clients</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Professional content creation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Automated follow-up sequences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Real-time analytics dashboard</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Ongoing campaign optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Weekly performance reports</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-6">
                  <h3 className="text-xl font-bold mb-2 text-center">Performance-Based Pricing</h3>
                  <div className="bg-golden/20 p-4 rounded-lg border border-golden/30 mb-4">
                    <p className="text-center font-bold text-white">
                      We Only Win When You Win
                    </p>
                    <p className="text-center text-sm text-gray-300">
                      Pay only for results, with a small setup fee
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">One-time setup fee:</span>
                      <span className="text-golden font-bold">${setupFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Performance fee:</span>
                      <span className="text-golden font-bold">To be discussed (€200-350 range)</span>
                    </div>
                    <p className="text-center text-gray-300 text-sm mt-4">
                      No monthly retainer - you only pay when we deliver results
                    </p>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-2 text-center">ROI Highlights</h3>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-golden">{roi}%+</p>
                      <p className="text-sm text-gray-300">Potential ROI</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">{paybackPeriod} Days</p>
                      <p className="text-sm text-gray-300">Average Payback</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">Ready to Transform Your Lead Generation?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Let's discuss how our lead generation system can work for Beyond Data Consulting and start delivering qualified leads within days.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/beyond-data-consulting-setup-call"
                  className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Your Strategy Call
                </a>
                <button
                  onClick={() => setShowCalculator(true)}
                  className="px-6 py-3 border border-gray-600 text-gray-300 rounded-full font-semibold hover:border-gray-400 hover:text-white transition-all duration-300"
                >
                  View ROI Details
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ROI Calculator Popup */}
        <AnimatePresence>
          {showCalculator && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
              onClick={() => setShowCalculator(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-black/90 rounded-2xl p-6 max-w-xl w-full border border-golden/30 m-4"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">ROI Analysis</h2>
                  <button
                    onClick={() => setShowCalculator(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                    <h3 className="font-semibold text-golden mb-1 text-sm">Initial Investment</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Setup Fee:</span>
                        <span className="font-bold">${setupFee.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Monthly Commission:</span>
                        <span className="font-bold">5-10% per lead</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                    <h3 className="font-semibold text-golden mb-1 text-sm">Monthly Benefits</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Leads Generated:</span>
                        <span className="font-bold">{monthlyLeadsHandled.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Potential Revenue:</span>
                        <span className="font-bold">${monthlySavings.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                    <h3 className="font-semibold text-golden mb-1 text-sm">Annual Benefits</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Potential Revenue:</span>
                        <span className="font-bold">${annualSavings.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Our Commission:</span>
                        <span className="font-bold">Based on success</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                    <h3 className="font-semibold text-golden mb-1 text-sm">ROI Metrics</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Potential ROI:</span>
                        <span className="font-bold">{roi}%+</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Payback Period:</span>
                        <span className="font-bold">{paybackPeriod} Days</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-golden/20 to-black/30 p-3 rounded-xl border border-golden/30 mb-4">
                  <div className="flex items-start">
                    <Lightbulb className="w-5 h-5 text-golden mr-2 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold mb-1 text-sm">Performance-Based Model</h3>
                      <p className="text-xs text-gray-300">
                        Our unique performance-based pricing model means we only succeed when you do. After the initial setup fee, you only pay a small percentage of the value we generate through successful leads, ensuring perfect alignment of interests.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <a
                    href="https://cal.com/dsmarketing.meeting.with.pascoal/beyond-data-consulting-setup-call"
                    className="inline-flex items-center px-5 py-2 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 text-sm"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Your Strategy Call
                  </a>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Testimonial Popup */}
        <AnimatePresence>
          {showTestimonial && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
              onClick={() => setShowTestimonial(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-black/90 rounded-2xl p-6 max-w-xl w-full border border-golden/30 m-4"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Client Success Stories</h2>
                  <button
                    onClick={() => setShowTestimonial(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                    <p className="text-gray-300 italic mb-3 text-sm">
                      "The lead generation system has transformed our consulting business. We're now connecting with decision-makers we couldn't reach before, and our sales team is focused on closing deals rather than prospecting."
                    </p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80" 
                        alt="Consulting Professional" 
                        className="w-10 h-10 rounded-full object-cover mr-3"
                      />
                      <div>
                        <p className="font-semibold text-sm">Michael Chen</p>
                        <p className="text-xs text-gray-400">CEO, DataMetrics Consulting</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                    <p className="text-gray-300 italic mb-3 text-sm">
                      "The quality of leads we're getting is remarkable. These are companies actively looking for data consulting services, and the personalized approach makes all the difference in getting responses from busy executives."
                    </p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80" 
                        alt="Consulting Professional" 
                        className="w-10 h-10 rounded-full object-cover mr-3"
                      />
                      <div>
                        <p className="font-semibold text-sm">Sarah Johnson</p>
                        <p className="text-xs text-gray-400">Director, Analytics Solutions</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                    <p className="text-gray-300 italic mb-3 text-sm">
                      "The ROI has been remarkable. Not only have we reduced our business development costs, but we've also seen a significant increase in qualified opportunities. The system has paid for itself within the first month."
                    </p>
                    <div className="flex items-center">
                      <img 
                        src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80" 
                        alt="Consulting Professional" 
                        className="w-10 h-10 rounded-full object-cover mr-3"
                      />
                      <div>
                        <p className="font-semibold text-sm">James Wilson</p>
                        <p className="text-xs text-gray-400">Founder, DataSphere Consulting</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 text-center">
                  <a
                    href="https://cal.com/dsmarketing.meeting.with.pascoal/beyond-data-consulting-setup-call"
                    className="inline-flex items-center px-5 py-2 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 text-sm"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Your Strategy Call
                  </a>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}